/**
 * Activity List — displays the user's recorded outings.
 *
 * Rendered inside the Logbook's "Activities" tab. Uses activityStorage
 * for CRUD operations on the new graph-model Activity entity.
 */

import { logger } from '../../utils/logger.js'
import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { activityStorage } from '../../storage/activity-storage.js'
import { backfillPlanAssociations } from '../../services/plan-association-service.js'
import { countsBySubject } from '../../storage/relationship-counts.js'
import { router } from '../../utils/router.js'
import type { Activity, ActivityType } from '../../schemas/activity.js'

import '@material/web/iconbutton/icon-button.js'
import '@material/web/progress/linear-progress.js'
import '../common/confirm-dialog'
import type { ConfirmDialog } from '../common/confirm-dialog.js'

import { authService } from '../../services/auth/auth-service.js'
import type { AuthState } from '../../services/auth/auth-context.js'
import { syncListOnNavigate } from '../../storage/sync-manager.js'

const log = logger.create('activity-list')

const ACTIVITY_ICONS: Record<ActivityType, string> = {
  hike: 'hiking',
  run: 'directions_run',
  bike: 'directions_bike',
  ski: 'downhill_skiing',
  paddle: 'kayaking',
  other: 'explore',
}

@customElement('activity-list')
export class ActivityList extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
    }

    .sync-bar {
      --md-linear-progress-active-indicator-color: var(--cairn-primary-color, #3b82f6);
      --md-linear-progress-track-color: transparent;
      --md-linear-progress-track-height: 2px;
      --md-linear-progress-active-indicator-height: 2px;
      width: 100%;
      flex-shrink: 0;
    }

    .list-content {
      flex: 1;
      padding: var(--cairn-spacing-md, 16px);
      overflow-y: auto;
      min-height: 0;
      transition: transform 0.15s ease-out;
    }

    .controls {
      display: flex;
      gap: var(--cairn-spacing-sm, 8px);
      align-items: center;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .search-input {
      flex: 1;
      padding: 10px 12px;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      font-size: var(--cairn-text-size-sm, 14px);
      background: var(--cairn-surface-color, #ffffff);
      color: var(--cairn-text-color, #1e293b);
      outline: none;
      transition: border-color var(--cairn-animation-duration, 0.2s);
    }

    .search-input:focus {
      border-color: var(--cairn-primary-color, #3b82f6);
    }

    .search-input::placeholder {
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .activity-grid {
      display: grid;
      gap: 16px;
      grid-template-columns: 1fr;
    }

    @media (min-width: 901px) and (max-width: 1300px) {
      .activity-grid {
        grid-template-columns: 1fr 1fr;
      }
    }

    @media (min-width: 1301px) {
      .activity-grid {
        grid-template-columns: 1fr 1fr 1fr;
      }
    }

    .activity-card {
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      padding: var(--cairn-spacing-md, 16px);
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s);
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-sm, 8px);
      box-sizing: border-box;
    }

    .activity-card:hover {
      border-color: var(--cairn-primary-color, #3b82f6);
      box-shadow: var(--cairn-shadow, 0 2px 4px rgba(0, 0, 0, 0.1));
    }

    .card-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--cairn-spacing-sm, 8px);
    }

    .card-title {
      flex: 1;
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-sm, 8px);
    }

    .type-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: var(--cairn-primary-color, #3b82f6);
      color: white;
      flex-shrink: 0;
    }

    .type-icon .material-icons {
      font-size: 20px;
    }

    .activity-name {
      font-size: var(--cairn-text-size-lg, 18px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    .activity-type {
      font-size: 12px;
      text-transform: capitalize;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .meta {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .meta .material-icons {
      font-size: 16px;
    }

    .description {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      line-height: 1.4;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
    }

    .related-counts {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .count-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      padding: 2px 8px;
      border-radius: 12px;
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .count-chip .material-icons {
      font-size: 14px;
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: var(--cairn-spacing-xl, 32px);
      min-height: 300px;
    }

    .empty-icon {
      font-family: 'Material Symbols Outlined';
      font-size: 48px;
      margin-bottom: var(--cairn-spacing-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
      opacity: 0.6;
    }

    .empty-title {
      font-size: var(--cairn-text-size-lg, 20px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-sm, 8px) 0;
    }

    .empty-description {
      font-size: var(--cairn-text-size-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin-bottom: var(--cairn-spacing-lg, 24px);
      max-width: 400px;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      line-height: 1;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      word-wrap: normal;
      direction: ltr;
      user-select: none;
    }
  `

  @state() private activities: Activity[] = []
  @state() private loaded = false
  @state() private syncing = false
  @state() private searchQuery = ''
  @state() private planCountByActivity: Map<string, number> = new Map()
  @state() private routeCountByActivity: Map<string, number> = new Map()

  private authState: AuthState = authService.getState()
  private unsubAuth?: () => void

  override connectedCallback(): void {
    super.connectedCallback()
    this.authState = authService.getState()
    this.unsubAuth = authService.onChange(state => {
      this.authState = state
      void this.loadActivities()
    })
    void this.loadActivities()
    this.syncing = true
    void syncListOnNavigate('activities')
      ?.then(() => this.loadActivities())
      .finally(() => {
        this.syncing = false
      })
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.unsubAuth?.()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  }

  private handleVisibilityChange = (): void => {
    if (!document.hidden) {
      void this.loadActivities()
    }
  }

  private sortByStartDesc(activities: Activity[]): Activity[] {
    return [...activities].sort((a, b) => {
      const aTime = a.startTime ? new Date(a.startTime).getTime() : 0
      const bTime = b.startTime ? new Date(b.startTime).getTime() : 0
      return bTime - aTime
    })
  }

  private async loadActivities(): Promise<void> {
    try {
      const result = await activityStorage.getActivitiesForUser(this.authState.userId)
      if (result.success && result.data) {
        this.activities = this.sortByStartDesc(result.data)
      }
      await this.loadCounts()
    } catch (error) {
      log.error('Failed to load activities:', error)
    } finally {
      this.loaded = true
    }

    // Self-heal in the background AFTER the list is shown: associate any
    // unassociated activities with a matching plan (and apply "Track N of
    // {plan}" names), then refresh if anything changed. Never blocks the list.
    try {
      const linked = await backfillPlanAssociations()
      if (linked > 0) {
        const refreshed = await activityStorage.getActivitiesForUser(this.authState.userId)
        if (refreshed.success && refreshed.data) {
          this.activities = this.sortByStartDesc(refreshed.data)
        }
      }
    } catch (error) {
      log.warn('Activity association backfill failed:', error)
    }
  }

  private async loadCounts(): Promise<void> {
    try {
      const [planCounts, routeCounts] = await Promise.all([
        countsBySubject('linked-to-plan', 'activity'),
        countsBySubject('follows-route', 'activity'),
      ])
      this.planCountByActivity = planCounts
      this.routeCountByActivity = routeCounts
    } catch (error) {
      log.error('Failed to load related counts:', error)
    }
  }

  private pullStartY = 0
  private pulling = false
  private scrollerEl: HTMLElement | null = null

  private handleTouchStart(e: TouchEvent): void {
    const scroller = e.currentTarget as HTMLElement
    this.scrollerEl = scroller
    if (scroller.scrollTop <= 0) {
      this.pullStartY = e.touches[0].clientY
      this.pulling = true
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.pulling || !this.scrollerEl) return
    const dy = e.touches[0].clientY - this.pullStartY
    if (dy > 0) {
      const clamped = Math.min(dy * 0.4, 80)
      this.scrollerEl.style.transform = `translateY(${clamped}px)`
    } else {
      this.pulling = false
      this.scrollerEl.style.transform = ''
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.pulling) return
    const dy = e.changedTouches[0].clientY - this.pullStartY
    this.pulling = false
    if (this.scrollerEl) this.scrollerEl.style.transform = ''
    if (dy > 60) {
      void this.refresh()
    }
  }

  private async refresh(): Promise<void> {
    this.syncing = true
    try {
      await syncListOnNavigate('activities')
      await this.loadActivities()
    } finally {
      this.syncing = false
    }
  }

  private renderCounts(planCount: number, routeCount: number): unknown {
    if (planCount === 0 && routeCount === 0) return ''
    return html`
      <div class="related-counts">
        ${planCount > 0
          ? html`
              <span class="count-chip">
                <span class="material-icons">map</span>
                ${planCount} ${planCount === 1 ? 'Plan' : 'Plans'}
              </span>
            `
          : ''}
        ${routeCount > 0
          ? html`
              <span class="count-chip">
                <span class="material-icons">alt_route</span>
                ${routeCount} ${routeCount === 1 ? 'Route' : 'Routes'}
              </span>
            `
          : ''}
      </div>
    `
  }

  private get filteredActivities(): Activity[] {
    if (!this.searchQuery.trim()) return this.activities
    const q = this.searchQuery.toLowerCase().trim()
    return this.activities.filter(
      a =>
        (a.name?.toLowerCase().includes(q) ||
          a.activityType.toLowerCase().includes(q) ||
          a.description?.toLowerCase().includes(q)) ??
        a.conditions?.toLowerCase().includes(q)
    )
  }

  private handleSearchChange(e: Event): void {
    this.searchQuery = (e.target as HTMLInputElement).value
  }

  private handleViewActivity(activity: Activity): void {
    router.navigate(`/activities/${activity.id}`)
  }

  private async handleDeleteActivity(activity: Activity): Promise<void> {
    const dialog = this.shadowRoot?.querySelector('confirm-dialog') as ConfirmDialog | undefined
    if (dialog) {
      dialog.headline = 'Delete Activity'
      dialog.message = `Delete "${activity.name ?? 'this activity'}"? This will also delete associated track data and cannot be undone.`
      const confirmed = await dialog.show()
      if (!confirmed) return
    }
    try {
      await activityStorage.deleteActivity(activity.id)
      await this.loadActivities()
    } catch (error) {
      log.error('Failed to delete activity:', error)
    }
  }

  private formatDateTime(dateString?: string): string {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  private renderActivityCard(activity: Activity): unknown {
    const icon = ACTIVITY_ICONS[activity.activityType]
    const planCount = this.planCountByActivity.get(activity.id) ?? 0
    const routeCount = this.routeCountByActivity.get(activity.id) ?? 0

    return html`
      <div class="activity-card" @click=${() => this.handleViewActivity(activity)}>
        <div class="card-header">
          <div class="card-title">
            <div class="type-icon">
              <span class="material-icons">${icon}</span>
            </div>
            <div>
              <h3 class="activity-name">
                ${activity.name ?? this.formatDateTime(activity.startTime)}
              </h3>
              <span class="activity-type">${activity.activityType}</span>
            </div>
          </div>
          <div @click=${(e: Event) => e.stopPropagation()}>
            <md-icon-button
              @click=${() => this.handleDeleteActivity(activity)}
              aria-label="Delete activity"
            >
              <span class="material-icons">delete</span>
            </md-icon-button>
          </div>
        </div>

        ${activity.startTime
          ? html`
              <div class="meta">
                <span class="material-icons">schedule</span>
                ${this.formatDateTime(activity.startTime)}
                ${activity.endTime ? html`&ndash; ${this.formatDateTime(activity.endTime)}` : ''}
              </div>
            `
          : ''}
        ${activity.conditions
          ? html`
              <div class="meta">
                <span class="material-icons">cloud</span>
                ${activity.conditions}
              </div>
            `
          : ''}
        ${activity.description ? html`<div class="description">${activity.description}</div>` : ''}
        ${this.renderCounts(planCount, routeCount)}
      </div>
    `
  }

  override render(): unknown {
    return html`
      ${this.syncing
        ? html`<md-linear-progress indeterminate class="sync-bar"></md-linear-progress>`
        : nothing}
      <div
        class="list-content"
        @touchstart=${this.handleTouchStart}
        @touchmove=${this.handleTouchMove}
        @touchend=${this.handleTouchEnd}
      >
        ${this.loaded && this.activities.length === 0
          ? html`
              <div class="empty-state">
                <div class="empty-icon">directions_walk</div>
                <h2 class="empty-title">No activities yet</h2>
                <p class="empty-description">
                  Record your next outing! Hit the record button on the map to start tracking.
                </p>
              </div>
            `
          : html`
              <div class="controls">
                <input
                  class="search-input"
                  type="text"
                  placeholder="Search activities..."
                  .value=${this.searchQuery}
                  @input=${this.handleSearchChange}
                />
              </div>

              ${this.filteredActivities.length === 0
                ? html`
                    <div class="empty-state">
                      <div class="empty-icon">search_off</div>
                      <h2 class="empty-title">No activities found</h2>
                      <p class="empty-description">Try adjusting your search.</p>
                    </div>
                  `
                : html`
                    <div class="activity-grid">
                      ${this.filteredActivities.map(a => this.renderActivityCard(a))}
                    </div>
                  `}
            `}
      </div>
      <confirm-dialog></confirm-dialog>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'activity-list': ActivityList
  }
}
